import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type {
  MaturityEvaluation,
  MaturityFrameworkResponse,
  MaturityEvaluationRubric,
  PageView,
  SaveStatus,
} from "@/types/maturity";
import { DOMAINS_META } from "@/utils/maturity-colors";
import {
  getApplicableRequirements,
  getAnsweredRequirementCount,
} from "@/utils/maturity-scoring";
import {
  useGetEvaluationRubrics,
  usePrefetchEvaluationRubrics,
} from "@/hooks/api/query/useGetEvaluationRubrics";
import { useGetScoringAlgorithm } from "@/hooks/api/query/useGetScoringAlgorithm";
import { useSaveMaturityFull } from "@/hooks/api/mutate/useSaveMaturityFull";
import { useFinalizeMaturityFull } from "@/hooks/api/mutate/useFinalizeMaturityFull";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import { PaywallDialog } from "@/components/PaywallDialog";
import { PaywallButton } from "@/components/ui/PaywallButton";
import { CREDIT_ACTION_TYPES } from "@/constants/credit";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";
import { SaveIndicator } from "../SaveIndicator";
import { FullEvalHeader } from "../FullEvalHeader";
import { FullEvalDomainTabs } from "../FullEvalDomainTabs";
import { FullEvalCategorySection } from "../FullEvalCategorySection";

type FullEvalViewProps = {
  evaluation: MaturityEvaluation;
  framework: MaturityFrameworkResponse;
  onNavigate: (view: PageView) => void;
};

function FullEvalView({
  evaluation,
  framework,
  onNavigate,
}: FullEvalViewProps) {
  const [activeDomain, setActiveDomain] = useState(0);
  const [localResponses, setLocalResponses] = useState<Record<string, number>>(
    () => ({ ...evaluation.full_responses })
  );
  const [localNotes, setLocalNotes] = useState<Record<string, string>>(() => ({
    ...evaluation.full_notes,
  }));
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [showPaywall, setShowPaywall] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const saveMutation = useSaveMaturityFull(evaluation.form_session_id);
  const finalizeMutation = useFinalizeMaturityFull(evaluation.form_session_id);
  const { hasInsufficientCredits } = useCheckActionCredits(
    {
      session_id: evaluation.form_session_id,
      action_type: CREDIT_ACTION_TYPES.CALCUL_MATURITATE_V2,
    },
    !!evaluation.form_session_id
  );

  const saveMutationRef = useRef(saveMutation);
  useEffect(() => {
    saveMutationRef.current = saveMutation;
  }, [saveMutation]);

  const finalizeMutationRef = useRef(finalizeMutation);
  useEffect(() => {
    finalizeMutationRef.current = finalizeMutation;
  }, [finalizeMutation]);

  const { data: scoringAlgorithm } = useGetScoringAlgorithm();

  const activeDomainCode = framework.activities[activeDomain]?.code ?? "";

  const { data: rubricsData } = useGetEvaluationRubrics(
    undefined,
    activeDomainCode
  );
  const nextDomainCode = framework.activities[activeDomain + 1]?.code;
  usePrefetchEvaluationRubrics(nextDomainCode);

  const allRubrics: MaturityEvaluationRubric[] = useMemo(
    () => rubricsData?.maturity_evaluations ?? [],
    [rubricsData]
  );

  const rubricsByCode = useMemo(() => {
    const map = new Map<string, MaturityEvaluationRubric[]>();
    for (const rubric of allRubrics) {
      const arr = map.get(rubric.requirement_code) ?? [];
      arr.push(rubric);
      map.set(rubric.requirement_code, arr);
    }
    return map;
  }, [allRubrics]);

  const applicableRequirements = useMemo(
    () =>
      getApplicableRequirements(
        framework.requirements,
        evaluation.assurance_level
      ),
    [framework.requirements, evaluation.assurance_level]
  );

  const keyMeasureCodes = useMemo(() => {
    if (!scoringAlgorithm) return [];
    const levelConfig = scoringAlgorithm.assurance_levels.find(
      (l) => l.code === evaluation.assurance_level
    );
    return levelConfig?.key_measures ?? [];
  }, [scoringAlgorithm, evaluation.assurance_level]);

  const domainProgress = useMemo(() => {
    return framework.activities.map((activity) => {
      const domainCategories = framework.categories.filter(
        (c) => c.activity_code === activity.code
      );
      const domainSubcategories = framework.subcategories.filter((s) =>
        domainCategories.some((c) => c.code === s.category_code)
      );
      const domainReqs = applicableRequirements.filter((r) =>
        domainSubcategories.some((s) => s.code === r.subcategory_code)
      );
      const answered = getAnsweredRequirementCount(localResponses, domainReqs);
      return { answered, total: domainReqs.length };
    });
  }, [
    framework.activities,
    framework.categories,
    framework.subcategories,
    applicableRequirements,
    localResponses,
  ]);

  const totalAnswered = useMemo(
    () => domainProgress.reduce((sum, d) => sum + d.answered, 0),
    [domainProgress]
  );

  const totalReqs = useMemo(
    () => domainProgress.reduce((sum, d) => sum + d.total, 0),
    [domainProgress]
  );

  const allAnswered = totalAnswered === totalReqs && totalReqs > 0;
  const isLastDomain = activeDomain === framework.activities.length - 1;

  const localNotesRef = useRef(localNotes);
  useEffect(() => {
    localNotesRef.current = localNotes;
  }, [localNotes]);

  const localResponsesRef = useRef(localResponses);
  useEffect(() => {
    localResponsesRef.current = localResponses;
  }, [localResponses]);

  const debouncedSave = useCallback(
    (
      newResponses: Record<string, number>,
      newNotes: Record<string, string>
    ) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setSaveStatus("saving");
      timeoutRef.current = setTimeout(() => {
        saveMutationRef.current.mutate(
          { responses: newResponses, notes: newNotes },
          {
            onSuccess: () => setSaveStatus("saved"),
            onError: () => setSaveStatus("error"),
          }
        );
      }, 300);
    },
    []
  );

  const flushSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setSaveStatus("saving");
    saveMutationRef.current.mutate(
      { responses: localResponsesRef.current, notes: localNotesRef.current },
      {
        onSuccess: () => setSaveStatus("saved"),
        onError: () => setSaveStatus("error"),
      }
    );
  }, []);

  const handleScoreChange = useCallback(
    (key: string, value: number) => {
      setLocalResponses((prev) => {
        const next = { ...prev, [key]: value };
        debouncedSave(next, localNotesRef.current);
        return next;
      });
    },
    [debouncedSave]
  );

  const handleNoteChange = useCallback(
    (key: string, value: string) => {
      setLocalNotes((prev) => {
        const next = { ...prev, [key]: value };
        debouncedSave(localResponsesRef.current, next);
        return next;
      });
    },
    [debouncedSave]
  );

  const isAlreadyFinalized = evaluation.eval_state === "FULL_COMPLETE";

  const handleFinalize = useCallback(() => {
    if (!isAlreadyFinalized && hasInsufficientCredits) {
      setShowPaywall(true);
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setSaveStatus("saving");
    saveMutationRef.current.mutate(
      { responses: localResponses, notes: localNotes },
      {
        onSuccess: () => {
          if (isAlreadyFinalized) {
            setSaveStatus("saved");
            onNavigate("summary");
            return;
          }
          finalizeMutationRef.current.mutate(undefined, {
            onSuccess: () => {
              setSaveStatus("saved");
              onNavigate("summary");
            },
            onError: (error) => {
              if (error instanceof InsufficientCreditsError) {
                setSaveStatus("idle");
                setShowPaywall(true);
              } else {
                setSaveStatus("error");
              }
            },
          });
        },
        onError: () => setSaveStatus("error"),
      }
    );
  }, [
    localResponses,
    localNotes,
    onNavigate,
    hasInsufficientCredits,
    isAlreadyFinalized,
  ]);

  const handlePrevDomain = useCallback(() => {
    if (activeDomain > 0) {
      setActiveDomain(activeDomain - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [activeDomain]);

  const handleNextDomain = useCallback(() => {
    if (activeDomain < framework.activities.length - 1) {
      setActiveDomain(activeDomain + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [activeDomain, framework.activities.length]);

  const activeDomainCategories = useMemo(
    () =>
      framework.categories.filter((c) => c.activity_code === activeDomainCode),
    [framework.categories, activeDomainCode]
  );

  const activeDomainSubcategories = useMemo(
    () =>
      framework.subcategories.filter((s) =>
        activeDomainCategories.some((c) => c.code === s.category_code)
      ),
    [framework.subcategories, activeDomainCategories]
  );

  const activeDomainRequirements = useMemo(
    () =>
      applicableRequirements.filter((r) =>
        activeDomainSubcategories.some((s) => s.code === r.subcategory_code)
      ),
    [applicableRequirements, activeDomainSubcategories]
  );

  const domainMeta = DOMAINS_META.find((d) => d.id === activeDomainCode);

  return (
    <div className="max-w-[1120px] mx-auto space-y-5">
      <FullEvalHeader answeredCount={totalAnswered} totalCount={totalReqs} />

      <FullEvalDomainTabs
        activities={framework.activities}
        activeDomain={activeDomain}
        onDomainChange={(index) => {
          setActiveDomain(index);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        domainProgress={domainProgress}
      />
      <div
        className="rounded-xl px-5 py-4 flex items-center justify-between"
        style={{
          borderLeft: `5px solid ${domainMeta?.color ?? "#6B7280"}`,
          background: `linear-gradient(135deg, ${domainMeta?.bg ?? "#F9FAFB"}, #fff)`,
        }}
      >
        <div>
          <h2
            className="text-lg font-extrabold"
            style={{ color: domainMeta?.color ?? "#374151" }}
          >
            {domainMeta?.name ?? activeDomainCode}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {activeDomainCategories.length} categorii ·{" "}
            {activeDomainRequirements.length} cerințe
          </p>
        </div>
        <SaveIndicator status={saveStatus} inline onRetry={flushSave} />
      </div>

      <div className="space-y-4">
        {activeDomainCategories.map((category) => (
          <FullEvalCategorySection
            key={category.code}
            category={category}
            subcategories={activeDomainSubcategories}
            requirements={activeDomainRequirements}
            keyMeasureCodes={keyMeasureCodes}
            responses={localResponses}
            notes={localNotes}
            rubricsByCode={rubricsByCode}
            domainColor={domainMeta?.color ?? "#6B7280"}
            domainBg={domainMeta?.bg ?? "#F9FAFB"}
            onScoreChange={handleScoreChange}
            onNoteChange={handleNoteChange}
            onNoteBlur={flushSave}
          />
        ))}
      </div>

      <div className="flex items-center justify-between pt-4 pb-8 border-t border-slate-200">
        <Button
          variant="outline"
          onClick={() => {
            if (activeDomain === 0) {
              onNavigate("summary");
            } else {
              handlePrevDomain();
            }
          }}
        >
          <ArrowLeft className="w-4 h-4" />
          {activeDomain === 0 ? "Înapoi" : "Domeniul anterior"}
        </Button>

        {isLastDomain && !allAnswered && (
          <p className="text-xs text-slate-500">
            Mai ai {totalReqs - totalAnswered} cerințe de completat pentru a
            finaliza.
          </p>
        )}

        <div className="flex items-center gap-3">
          {isLastDomain && allAnswered ? (
            <PaywallButton
              locked={
                !isAlreadyFinalized &&
                !(saveMutation.isPending || finalizeMutation.isPending) &&
                hasInsufficientCredits
              }
              leftIcon={<CheckCircle2 className="w-4 h-4" />}
              onClick={handleFinalize}
              disabled={saveMutation.isPending || finalizeMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saveMutation.isPending || finalizeMutation.isPending
                ? isAlreadyFinalized
                  ? "Se salvează..."
                  : "Se finalizează..."
                : isAlreadyFinalized
                  ? "Salvează și înapoi la sumar"
                  : "Finalizează evaluarea"}
            </PaywallButton>
          ) : (
            <Button onClick={handleNextDomain} disabled={isLastDomain}>
              Următorul
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <PaywallDialog
        open={showPaywall}
        onOpenChange={setShowPaywall}
        actionType="calcul_maturitate_v2"
      />
    </div>
  );
}

export default FullEvalView;
